import React, { useEffect, useState } from "react";
import { Accessibility, X, Type, Contrast, PauseCircle, Link2, BookOpen } from "lucide-react";

const DEFAULTS = { textSize: 0, contrast: false, reduceMotion: false, underlineLinks: false, readableFont: false };

const apply = (p) => {
  const html = document.documentElement;
  html.classList.toggle("a11y-text-lg", p.textSize === 1);
  html.classList.toggle("a11y-text-xl", p.textSize === 2);
  html.classList.toggle("a11y-contrast", p.contrast);
  html.classList.toggle("a11y-reduce-motion", p.reduceMotion);
  html.classList.toggle("a11y-underline-links", p.underlineLinks);
  html.classList.toggle("a11y-readable-font", p.readableFont);
};

export const AccessibilityWidget = () => {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(() => {
    try { return { ...DEFAULTS, ...JSON.parse(localStorage.getItem("mk_a11y") || "{}") }; } catch { return DEFAULTS; }
  });

  useEffect(() => { apply(prefs); localStorage.setItem("mk_a11y", JSON.stringify(prefs)); }, [prefs]);

  const Toggle = ({ icon: Icon, label, k, testid }) => (
    <button onClick={() => setPrefs({ ...prefs, [k]: !prefs[k] })} role="switch" aria-checked={prefs[k]}
      className={`w-full flex items-center justify-between gap-3 border p-3 text-sm transition-colors ${prefs[k] ? "border-[#0047AB] bg-[#0047AB]/5 text-[#0047AB]" : "border-neutral-200 hover:border-neutral-400"}`} data-testid={testid}>
      <span className="flex items-center gap-2"><Icon className="w-4 h-4" aria-hidden="true" />{label}</span>
      <span className={`text-[10px] uppercase tracking-wide ${prefs[k] ? "text-[#0047AB]" : "text-neutral-400"}`}>{prefs[k] ? "On" : "Off"}</span>
    </button>
  );

  return (
    <>
      <button onClick={() => setOpen(!open)} aria-label="Accessibility options" title="Accessibility options"
        className="fixed bottom-5 left-5 z-[120] w-12 h-12 rounded-full bg-[#0047AB] text-white flex items-center justify-center shadow-lg hover:bg-[#003380] transition-colors focus-visible:outline focus-visible:outline-4 focus-visible:outline-[#FFC107]"
        data-testid="a11y-widget-btn">
        <Accessibility className="w-6 h-6" aria-hidden="true" />
      </button>
      {open && (
        <div className="fixed bottom-20 left-5 z-[120] w-80 max-w-[calc(100vw-40px)] bg-white border border-neutral-200 shadow-2xl p-5" role="dialog" aria-label="Accessibility settings" data-testid="a11y-panel">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-heading text-lg">Accessibility</h2>
            <button onClick={() => setOpen(false)} aria-label="Close accessibility panel" className="p-1 hover:text-[#0047AB]" data-testid="a11y-close"><X className="w-4 h-4" /></button>
          </div>
          <p className="text-xs text-neutral-500 mb-4">Everyone is welcome here. Adjust the site to suit you — your settings are remembered.</p>
          <div className="mb-3">
            <span className="label-caps block mb-2 flex items-center gap-2"><Type className="w-3.5 h-3.5" aria-hidden="true" />Text size</span>
            <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Text size">
              {["Normal", "Large", "Larger"].map((l, i) => (
                <button key={l} onClick={() => setPrefs({ ...prefs, textSize: i })} role="radio" aria-checked={prefs.textSize === i}
                  className={`border py-2 text-xs transition-colors ${prefs.textSize === i ? "bg-[#0047AB] text-white border-[#0047AB]" : "border-neutral-200 hover:border-neutral-400"}`} data-testid={`a11y-text-${i}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Toggle icon={Contrast} label="High contrast" k="contrast" testid="a11y-contrast" />
            <Toggle icon={PauseCircle} label="Reduce motion & animations" k="reduceMotion" testid="a11y-motion" />
            <Toggle icon={Link2} label="Underline all links" k="underlineLinks" testid="a11y-links" />
            <Toggle icon={BookOpen} label="Easy-read font & spacing" k="readableFont" testid="a11y-font" />
          </div>
          <a href="/accessibility" className="block text-xs text-[#0047AB] underline mt-4" data-testid="a11y-statement-link">Read our accessibility commitment →</a>
        </div>
      )}
    </>
  );
};
